import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { Section } from './Section.js'
import { LotusFlower } from './LotusFlower.js'
import { TextReveal } from './ExperienceSection.js'

export class ProjectsSection extends Section {
  async build(camera, renderer) {
    super.build(camera, renderer)
    
    
    this.group.position.y = 0.5
    this.group.rotation.x = Math.PI * 0.05  // Tilt 15% towards camera
    
    // Track first click for overlay fade-in
    this.firstClickHappened = false
    
    // Background - White
    this.createBackground(0xd5d7d6)
    
    // Load GLB models
    await this.loadModels()
    
    // Setup click interaction
    this.setupInteraction()
    
    // Orbiting satellites (migrated from ExperienceSection)
    this.createSatellites()
    
    // Disc light at the bottom of the stand, pointing upward
    this.createDiscLight()
  }
  
  async loadModels() {
    const loader = new GLTFLoader()
    
    try {
      // Load stand.glb first (contains anchors)
      const standGltf = await new Promise((resolve, reject) => {
        loader.load('./sect02/stand.glb', resolve, undefined, reject)
      })
      this.standModel = standGltf.scene
      
      // Find basestand mesh and ground BEFORE setupModel (to check original positions)
      this.baseStandMesh = null
      this.clockwiseParts = []  
      this.counterClockwiseParts = []  
      this.cylinder001 = null
      this.groundMesh = null
      
      this.standModel.traverse((child) => {
        if (child.isMesh) {
          const name = child.name.toLowerCase()
          if (name === 'basestand') {
            this.baseStandMesh = child
          } else if (name === 'cylinder002' || name === 'cylinder004') {
            this.clockwiseParts.push(child)
          } else if (name === 'cylinder001' || name === 'cylinder003') {
            this.counterClockwiseParts.push(child)
            if (name === 'cylinder001') {
              this.cylinder001 = child
            }
          } else if (name === 'ground') {
            this.groundMesh = child
          }
        }
      })
      
      this.setupModel(this.standModel, { x: 0, y: -1.5, z: 3 }, 6.0)
      this.add(this.standModel)
      
      // Apply mask texture to ground mesh
      if (this.groundMesh) {
        const maskLoader = new THREE.TextureLoader()
        const groundMask = maskLoader.load('./sect02/ground_mask.webp')
        groundMask.colorSpace = THREE.NoColorSpace
        groundMask.flipY = false
        // Clone material so mask only affects ground mesh
        this.groundMesh.material = this.groundMesh.material.clone()
        this.groundMesh.material.alphaMap = groundMask
        this.groundMesh.material.transparent = true
        this.groundMesh.material.needsUpdate = true
      }
      
      // Noise generator parameters for cylinder001
      this.noiseTime = 0
      this.noiseSpeed = 1.0
      this.noiseAmplitude = 0.15
      
      // Find anchor points in stand model
      const anchors = []
      this.standModel.traverse((child) => {
        if (child.name.match(/lotus_anchor00[1-3]/)) {
          anchors.push(child)
        }
      })
      
      // Load lotus.glb as template
      const lotusGltf = await new Promise((resolve, reject) => {
        loader.load('./sect02/lotus.glb', resolve, undefined, reject)
      })
      
      // Instance lotus at each anchor position
      this.lotusFlowers = []
      this.lotusMeshes = [] // For raycasting
      
      // Load external texture for testing on lotus 1
      const textureLoader = new THREE.TextureLoader()
      const petalB = textureLoader.load('./sect02/petal_b_emission.webp')
      petalB.colorSpace = THREE.SRGBColorSpace
      petalB.flipY = false
      
      const petalC = textureLoader.load('./sect02/petal_c_emission.webp')
      petalC.colorSpace = THREE.SRGBColorSpace
      petalC.flipY = false
      
      // Use basestand as parent if found, otherwise fall back to standModel
      const lotusParent = this.baseStandMesh || this.standModel
      
      anchors.forEach((anchor, anchorIndex) => {
        // Get anchor's world position for the center of the lotus
        const anchorWorldPos = new THREE.Vector3()
        anchor.getWorldPosition(anchorWorldPos)
        
        // Convert world position to local position relative to lotusParent
        const localPos = anchorWorldPos.clone()
        lotusParent.worldToLocal(localPos)
        
        // Create lotus flower using embedded GLB texture + fresnel shader
        // Lotus 1 gets the external petal_b texture for testing
        const lotusFlower = new LotusFlower(lotusParent, lotusGltf, {
          petalCount: 6,
          layerCount: 6,
          layerRotationStep: 40,
          layerOffsetFrames: 25,
          petalScale: 1.5,
          map: anchorIndex === 1 ? petalB : anchorIndex === 2 ? petalC : null,
          lotusIndex: anchorIndex
        })
        
        // Position the lotus at the anchor location
        lotusFlower.setLocalPosition(localPos.x, localPos.y, localPos.z)
        
        // Collect meshes for raycasting
        this.lotusMeshes.push(...lotusFlower.getMeshes())
        
        this.lotusFlowers.push(lotusFlower)
      })
      
      // Hide anchor objects
      anchors.forEach(anchor => {
        anchor.visible = false
      })
      
      // Create info overlay planes above the red lotus (index 0)
      this.createInfoOverlays()
      
      // Create header text plane at top of section
      this.createHeaderPlane()
      
      // Target rotation for basestand (starts at 0)
      this.baseStandTargetRotation = 0
      this.baseStandCurrentRotation = 0
      
    } catch (error) {
      console.error('Failed to load models:', error)
    }
  }
  
  setupInteraction() {
    // Raycaster for click detection
    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
    this.activeLotusIndex = null // Track which lotus is currently animating
    
    this._onClick = this._onClick.bind(this)
    window.addEventListener('click', this._onClick)
    
    // Mobile tap support
    this._onTouchStart = this._onTouchStart.bind(this)
    this._onTouchEnd = this._onTouchEnd.bind(this)
    window.addEventListener('touchstart', this._onTouchStart, { passive: true })
    window.addEventListener('touchend', this._onTouchEnd, { passive: true })
  }
  
  _onTouchStart(event) {
    if (!this.isActive) return
    const touch = event.touches[0]
    this.touchStartX = touch.clientX
    this.touchStartY = touch.clientY
    this.touchStartTime = performance.now()
  }
  
  _onTouchEnd(event) {
    if (!this.isActive) return
    
    const touch = event.changedTouches[0]
    const dx = touch.clientX - this.touchStartX
    const dy = touch.clientY - this.touchStartY
    const dt = performance.now() - this.touchStartTime
    
    // Treat as a tap if minimal movement and short duration
    if (Math.abs(dx) < 15 && Math.abs(dy) < 15 && dt < 300) {
      this._onClick({ clientX: touch.clientX, clientY: touch.clientY })
    }
  }
  
  _onClick(event) {
    if (!this.isActive) return
    
    // Calculate mouse position
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
    
    // Get camera from scene
    const camera = this.scene.userData.camera
    if (!camera) return
    
    this.raycaster.setFromCamera(this.mouse, camera)
    
    // Check for overlay clicks first (they're in front)
    if (this.firstClickHappened && this.overlayMeshes) {
      const visibleOverlays = this.overlayMeshes.filter(m => m.visible)
      const overlayIntersects = this.raycaster.intersectObjects(visibleOverlays)
      
      if (overlayIntersects.length > 0) {
        const hit = overlayIntersects[0]
        const url = hit.object.userData.url
        if (url) {
          window.open(url, '_blank')
          return // Don't process lotus click if overlay was clicked
        }
      }
    }
    
    // Check for lotus clicks
    if (this.lotusMeshes.length === 0) return
    
    const intersects = this.raycaster.intersectObjects(this.lotusMeshes)
    
    if (intersects.length > 0) {
      const hit = intersects[0]
      const lotusIndex = hit.object.userData.lotusIndex
      
      if (lotusIndex !== undefined) {
        // Stop animation on previously active lotus
        if (this.activeLotusIndex !== null && this.activeLotusIndex !== lotusIndex) {
          this.lotusFlowers[this.activeLotusIndex].setHovered(false)
        }
        
        // Play animation on the clicked lotus
        this.lotusFlowers[lotusIndex].setHovered(true)
        this.activeLotusIndex = lotusIndex
        
        // Rotate stand to center the clicked lotus
        this.rotateToLotus(lotusIndex)
        
        // Fade in overlay on first click, then crossfade to selected lotus
        if (!this.firstClickHappened) {
          this.firstClickHappened = true
          this.fadeInInitialOverlay(lotusIndex)
        } else {
          // Crossfade to the selected lotus texture
          this.crossfadeToLotus(lotusIndex)
        }
      }
    }
  }
  
  createInfoOverlays() {
    // Load thumbnail atlas
    const textureLoader = new THREE.TextureLoader()
    const atlasTexture = textureLoader.load('./sect02/thumbnails_atlas.webp')
    atlasTexture.colorSpace = THREE.SRGBColorSpace
    
    // Use simple PlaneGeometry for clean texture mapping
    const planeWidth = 0.8
    const planeHeight = 0.45
    const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight)
    
    // URLs for each lotus
    const urls = [
      "https://proj.simplislick.com/#project07",
      "https://proj.simplislick.com/#project04",
      "https://proj.simplislick.com/#project02"
    ]
    
    // Create 3 overlay meshes, one for each lotus
    this.overlayMeshes = []
    this.currentOverlayIndex = 0 // Start with first lotus
    
    for (let index = 0; index < 3; index++) {
      // Slice the atlas for this lotus (stacked vertically: a=top, b=middle, c=bottom)
      const sliceTexture = atlasTexture.clone()
      sliceTexture.offset.set(0, (2 - index) / 3)
      sliceTexture.repeat.set(1, 1 / 3)
      sliceTexture.needsUpdate = true
      
      const material = new THREE.MeshBasicMaterial({
        map: sliceTexture,
        transparent: true,
        opacity: index === 0 ? 0 : 0, // Start invisible, will fade in on first click
        side: THREE.DoubleSide
      })
      
      const mesh = new THREE.Mesh(geometry, material)
      mesh.visible = index === 0 // Only first one visible initially
      mesh.scale.setScalar(1.5) // Scale up by 50%
      
      // Store URL for click handling
      mesh.userData = { url: urls[index], overlayIndex: index }
      
      // Add directly to scene (not group) so it stays at fixed world position
      this.scene.add(mesh)
      this.overlayMeshes.push(mesh)
    }
    
    // Position above red lotus (index 0) - will be set after lotus is positioned
    this.positionOverlaysAboveRedLotus()
  }
  
  positionOverlaysAboveRedLotus() {
    if (!this.overlayMeshes || !this.lotusFlowers || this.lotusFlowers.length < 1) return
    
    // Get red lotus (index 0) for initial position
    const redLotus = this.lotusFlowers[0]
    if (!redLotus || !redLotus.group) return
    
    // Get the lotus position in world space
    const lotusWorldPos = new THREE.Vector3()
    redLotus.group.getWorldPosition(lotusWorldPos)
    
    // Position all overlays at the same spot above the lotus in world space
    this.overlayMeshes.forEach(mesh => {
      mesh.position.set(lotusWorldPos.x, lotusWorldPos.y + 1.2, lotusWorldPos.z + 0.5)
    })
  }
  
  createHeaderPlane() {
    const textureLoader = new THREE.TextureLoader()
    const headerTexture = textureLoader.load('./sect02/sect02-header.webp')
    headerTexture.colorSpace = THREE.SRGBColorSpace
    
    const aspect = 278 / 93
    const height = 0.7
    const width = height * aspect
    
    const geometry = new THREE.PlaneGeometry(width, height)
    const material = new THREE.MeshBasicMaterial({
      map: headerTexture,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide
    })
    
    this.headerPlane = new THREE.Mesh(geometry, material)
    this.headerTargetY = 1
    this.headerStartY = 5.0
    this.headerPlane.position.set(0, this.headerStartY, 2)
    this.headerPlane.material.opacity = 0
    this.headerAnimated = false
    this.add(this.headerPlane)
  }
  
  crossfadeToLotus(lotusIndex) {
    if (!this.overlayMeshes || lotusIndex < 0 || lotusIndex >= this.overlayMeshes.length) return
    if (this.currentOverlayIndex === lotusIndex) return
    
    const duration = 500 // ms
    const startTime = performance.now()
    const fromMesh = this.overlayMeshes[this.currentOverlayIndex]
    const toMesh = this.overlayMeshes[lotusIndex]
    const fromMaterial = fromMesh.material
    const toMaterial = toMesh.material
    
    // Ensure target mesh is visible
    toMesh.visible = true
    
    const animate = () => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Ease in-out
      const ease = progress < 0.5 
        ? 2 * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 2) / 2
      
      fromMaterial.opacity = 0.85 * (1 - ease)
      toMaterial.opacity = 0.85 * ease
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        // Hide the old mesh after fade
        fromMesh.visible = false
        this.currentOverlayIndex = lotusIndex
      }
    }
    
    animate()
  }
  
  fadeInInitialOverlay(selectedLotusIndex) {
    if (!this.overlayMeshes) return
    
    // Set the current overlay index to the selected lotus
    this.currentOverlayIndex = selectedLotusIndex
    
    // Make only the selected overlay visible
    this.overlayMeshes.forEach((mesh, index) => {
      mesh.visible = index === selectedLotusIndex
      mesh.material.opacity = 0
    })
    
    const duration = 800 // ms
    const startTime = performance.now()
    const targetMesh = this.overlayMeshes[selectedLotusIndex]
    const targetMaterial = targetMesh.material
    
    const animate = () => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Ease out cubic
      const ease = 1 - Math.pow(1 - progress, 3)
      targetMaterial.opacity = 0.85 * ease
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }
    
    animate()
  }
  
  rotateToLotus(index) {
    if (!this.baseStandMesh) return
    
    // Calculate target rotation: each lotus is 120 degrees apart
    // Index 0 = 0°, Index 1 = 120°, Index 2 = 240° (-120°)
    const targetAngle = (index * 120) * (Math.PI / 180)
    this.baseStandTargetRotation = targetAngle
  }
  
  setupModel(model, position, targetSize) {
    const box = new THREE.Box3().setFromObject(model)
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())
    const maxDim = Math.max(size.x, size.y, size.z)
    const scale = targetSize / maxDim
    
    model.position.sub(center)
    model.scale.setScalar(scale)
    model.position.add(new THREE.Vector3(position.x, position.y, position.z))
    
    model.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true
        child.receiveShadow = true
      }
    })
  }
  
  updateBackgroundOpacity(cameraZ) {
    if (!this.backgroundPlane || !this.bgMaterial) return

    // Update plane position to follow camera
    const targetZ = cameraZ - this.cameraFollowDistance - this.bgZOffset
    this.backgroundPlane.position.z = targetZ

    // Delayed fade: only start appearing at z=-12, full at z=-20, gone by z=-28
    const fadeStart = -12
    const fadeCenter = -20
    const fadeEnd = -28
    let opacity
    if (cameraZ >= fadeStart) {
      opacity = 0
    } else if (cameraZ <= fadeEnd) {
      opacity = 0
    } else if (cameraZ > fadeCenter) {
      // Fading in: -12 → -20
      opacity = (fadeStart - cameraZ) / (fadeStart - fadeCenter)
    } else {
      // Fading out: -20 → -28
      opacity = (cameraZ - fadeEnd) / (fadeCenter - fadeEnd)
    }

    this.backgroundPlane.visible = opacity > 0.01
    this.bgMaterial.opacity = opacity
  }
  
  onEnter() {
    super.onEnter()
    
    // Show projects intro
    this.showProjectsIntro()
    
    // Entrance animation for models
    if (this.standModel) {
      this.standModel.scale.set(0, 0, 0)
      this.animateScale(this.standModel, 1, 0.8)
    }
    
    if (this.lotusFlowers) {
      this.lotusFlowers.forEach((lotus, index) => {
        lotus.animateEntrance(200 + index * 150, 0.6)
      })
    }
    
    // Reset header for entrance (animation triggers when camera settles)
    if (this.headerPlane) {
      this.headerPlane.visible = true
      this.headerPlane.position.y = this.headerStartY
      this.headerPlane.material.opacity = 0
      this.headerAnimated = false
    }
  }
  
  createSatellites() {
    this.satellites = []
    const satelliteCount = 8
    
    // Lantern emission palette from Hero section
    const palette = [
      new THREE.Color('rgb(0, 255, 128)'),
      new THREE.Color('rgb(255, 162, 23)'),
      new THREE.Color('rgb(255, 27, 122)')
    ]
    
    for (let i = 0; i < satelliteCount; i++) {
      const geometry = new THREE.ConeGeometry(0.08, 0.12, 4)
      const material = new THREE.MeshBasicMaterial({ color: palette[i % palette.length] })
      const satellite = new THREE.Mesh(geometry, material)
      
      satellite.userData = {
        angle: (i / satelliteCount) * Math.PI * 2,
        radius: 6 + Math.random() * 2,
        speed: 0.2 + Math.random() * 0.2,
        yOffset: (Math.random() - 0.5) * 4
      }
      
      this.add(satellite)
      this.satellites.push(satellite)
    }
  }
  
  createDiscLight() {
    // Position at the bottom of the stand (stand center is at y=-1.5, height ~6)
    const lightY = -4.5
    const lightPosition = new THREE.Vector3(0, lightY, 3)
    
    // Light source pointing upward
    const spotLight = new THREE.SpotLight(0x88ccff, 5, 15, Math.PI / 4, 0.5, 1)
    spotLight.position.copy(lightPosition)
    spotLight.target.position.set(lightPosition.x, lightPosition.y + 5, lightPosition.z)
    this.add(spotLight)
    this.add(spotLight.target)
  }
  
  // Pseudo-noise function using multiple sine waves (similar to Blender noise generator)
  noise(t) {
    // Combine multiple sine waves at different frequencies for noise-like effect
    return Math.sin(t * 1.0) * 0.5 +
           Math.sin(t * 2.3) * 0.25 +
           Math.sin(t * 4.7) * 0.125 +
           Math.sin(t * 9.1) * 0.0625
  }
  
  update(delta, scrollProgress, cameraZ, isCameraSettled) {
    if (!this.isActive) return
    
    // Fade out intro slightly before leaving the section
    const intro = document.getElementById('projects-intro')
    if (intro) {
      if (cameraZ < -23) {
        intro.classList.remove('visible')
      } else {
        intro.classList.add('visible')
      }
    }
    
    // Rotate cylinders (basestand is static)
    const rotationSpeed = delta * 0.3
    
    // Update noise time
    this.noiseTime += delta * this.noiseSpeed
    
    // Apply noise rotation to all cylinders with unique offsets
    const applyNoise = (part, ySpeed, noiseOffset) => {
      const noiseValue = this.noise(this.noiseTime + noiseOffset)
      part.rotation.y += ySpeed
      part.rotation.z += noiseValue * this.noiseAmplitude * delta * 0.5
    }
    
    // Clockwise cylinders: cylinder002, cylinder004
    this.clockwiseParts.forEach((part, index) => {
      applyNoise(part, rotationSpeed, index * 10) // Different noise offset for each
    })
    
    // Counter-clockwise cylinders: cylinder001, cylinder003
    this.counterClockwiseParts.forEach((part) => {
      if (part === this.cylinder001) {
        // cylinder001: 2x speed + noise
        applyNoise(part, -rotationSpeed * 2, 20)
      } else {
        // cylinder003: normal speed + noise
        applyNoise(part, -rotationSpeed, 30)
      }
    })
    
    // Smoothly rotate basestand to target (quick like a round table)
    if (this.baseStandMesh && Math.abs(this.baseStandTargetRotation - this.baseStandCurrentRotation) > 0.01) {
      let diff = this.baseStandTargetRotation - this.baseStandCurrentRotation
      // Normalize to -PI to +PI for shortest path rotation
      while (diff > Math.PI) diff -= 2 * Math.PI
      while (diff < -Math.PI) diff += 2 * Math.PI
      this.baseStandCurrentRotation += diff * delta * 3
      this.baseStandMesh.rotation.y = this.baseStandCurrentRotation
    }
    
    // Update lotus flowers (animations and bobbing)
    const time = performance.now() * 0.001
    if (this.lotusFlowers) {
      this.lotusFlowers.forEach(lotus => lotus.update(delta, time))
    }
    
    // Orbit satellites
    if (this.satellites) {
      this.satellites.forEach(satellite => {
        satellite.userData.angle += delta * satellite.userData.speed
        satellite.position.x = Math.cos(satellite.userData.angle) * satellite.userData.radius
        satellite.position.z = Math.sin(satellite.userData.angle) * satellite.userData.radius * 0.3
        satellite.position.y = satellite.userData.yOffset + Math.sin(time + satellite.userData.angle) * 0.3
        satellite.rotation.x += delta
        satellite.rotation.y += delta * 0.5
      })
    }
    
    // Header entrance: snap opacity and animate position once camera is near settled
    const cameraTarget = this.zPosition + 5
    const isNearSettled = Math.abs(cameraZ - cameraTarget) < 1.5
    if (isNearSettled && this.headerPlane && !this.headerAnimated) {
      this.headerPlane.material.opacity = 1
      this.animateHeaderPosition(this.headerTargetY, 0.8)
      this.headerAnimated = true
    }
    
    // Subtle rotation on overlay planes
    this.updateOverlayRotation(time)
  }
  
  updateOverlayRotation(time) {
    if (!this.overlayMeshes) return
    
    // Apply subtle Y and X rotation to visible overlays
    this.overlayMeshes.forEach(mesh => {
      if (mesh.visible) {
        // Subtle Y rotation (slow sway)
        mesh.rotation.y = Math.sin(time * 0.5) * 0.08 // +/- 0.08 radians (~4.5 degrees)
        // Subtle X rotation (gentle tilt)
        mesh.rotation.x = Math.cos(time * 0.3) * 0.05 // +/- 0.05 radians (~3 degrees)
      }
    })
  }
  
  onExit() {
    super.onExit()
    // Hide projects intro
    this.hideProjectsIntro()
    // Hide overlay meshes when leaving the section
    if (this.overlayMeshes) {
      this.overlayMeshes.forEach(mesh => {
        mesh.visible = false
      })
    }
    // Header exit animation: move up and fade out
    if (this.headerPlane) {
      this.animateHeader(this.headerStartY, 0.5, false)
    }
  }
  
  showProjectsIntro() {
    const intro = document.getElementById('projects-intro')
    if (intro) intro.classList.add('visible')
    
    const hintEl = document.getElementById('projects-hint-text')
    if (hintEl) {
      if (!this.hintTextReveal) {
        this.hintTextReveal = new TextReveal(hintEl, 'left-to-right')
      }
      setTimeout(() => this.hintTextReveal.reset(), 100)
    }
  }
  
  hideProjectsIntro() {
    const intro = document.getElementById('projects-intro')
    if (intro) intro.classList.remove('visible')
    
    if (this.hintTextReveal) {
      this.hintTextReveal.restore()
      this.hintTextReveal = null
    }
  }

  animateScale(object, target, duration) {
    const start = object.scale.x || 0
    const startTime = performance.now()
    
    const animate = () => {
      const elapsed = (performance.now() - startTime) / 1000
      const progress = Math.min(elapsed / duration, 1)
      const ease = 1 - Math.pow(1 - progress, 3)
      
      const current = start + (target - start) * ease
      object.scale.set(current, current, current)
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }
    
    animate()
  }
  
  animateHeaderPosition(targetY, duration) {
    if (this._headerRaf) {
      cancelAnimationFrame(this._headerRaf)
      this._headerRaf = null
    }
    
    const startY = this.headerPlane.position.y
    const startTime = performance.now()
    
    this.headerPlane.visible = true
    
    const animate = () => {
      const elapsed = (performance.now() - startTime) / 1000
      const progress = Math.min(elapsed / duration, 1)
      const ease = 1 - Math.pow(1 - progress, 3)
      
      this.headerPlane.position.y = startY + (targetY - startY) * ease
      
      if (progress < 1) {
        this._headerRaf = requestAnimationFrame(animate)
      } else {
        this._headerRaf = null
      }
    }
    
    this._headerRaf = requestAnimationFrame(animate)
  }
  
  animateHeader(targetY, duration, fadeIn) {
    if (this._headerRaf) {
      cancelAnimationFrame(this._headerRaf)
      this._headerRaf = null
    }
    
    const startY = this.headerPlane.position.y
    const startOpacity = this.headerPlane.material.opacity
    const targetOpacity = fadeIn ? 1 : 0
    const startTime = performance.now()
    
    this.headerPlane.visible = true
    
    const animate = () => {
      const elapsed = (performance.now() - startTime) / 1000
      const progress = Math.min(elapsed / duration, 1)
      const ease = 1 - Math.pow(1 - progress, 3)
      
      this.headerPlane.position.y = startY + (targetY - startY) * ease
      this.headerPlane.material.opacity = startOpacity + (targetOpacity - startOpacity) * ease
      
      if (progress < 1) {
        this._headerRaf = requestAnimationFrame(animate)
      } else {
        this._headerRaf = null
        if (!fadeIn) this.headerPlane.visible = false
      }
    }
    
    this._headerRaf = requestAnimationFrame(animate)
  }
  
  destroy() {
    // Destroy lotus flowers
    if (this.lotusFlowers) {
      this.lotusFlowers.forEach(lotus => lotus.destroy())
      this.lotusFlowers = []
    }
    
    // Cleanup satellites
    if (this.satellites) {
      this.satellites.forEach(satellite => {
        if (satellite.geometry) satellite.geometry.dispose()
        if (satellite.material) satellite.material.dispose()
        if (this.group) this.group.remove(satellite)
      })
      this.satellites = null
    }
    
    // Cleanup overlays
    if (this.overlayMeshes) {
      this.overlayMeshes.forEach(mesh => {
        mesh.geometry.dispose()
        if (mesh.material.map) mesh.material.map.dispose()
        mesh.material.dispose()
        if (this.scene) {
          this.scene.remove(mesh)
        }
      })
      this.overlayMeshes = null
    }
    
    // Cleanup header plane
    if (this.headerPlane) {
      if (this._headerRaf) {
        cancelAnimationFrame(this._headerRaf)
        this._headerRaf = null
      }
      this.headerPlane.geometry.dispose()
      if (this.headerPlane.material.map) this.headerPlane.material.map.dispose()
      this.headerPlane.material.dispose()
      if (this.group) this.group.remove(this.headerPlane)
      const idx = this.objects.indexOf(this.headerPlane)
      if (idx !== -1) this.objects.splice(idx, 1)
      this.headerPlane = null
    }
    
    window.removeEventListener('click', this._onClick)
    window.removeEventListener('touchstart', this._onTouchStart)
    window.removeEventListener('touchend', this._onTouchEnd)
    super.destroy()
  }
}
